import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Alert,
  Badge,
} from "reactstrap";
import OnlyHeader from "components/Headers/OnlyHeader";
import {
  getChatbotSettings,
  updateChatbotSettings,
} from "store/actions/chatbotKnowledgeAction";

const ChatbotSettings = () => {
  const dispatch = useDispatch();

  const { settings, settingsLoader, saveSettingsLoader } = useSelector(
    (state) => state.chatbotKnowledge
  );

  const [formData, setFormData] = useState({
    adminEmail: "",
    supportEmail: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(getChatbotSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings) {
      setFormData({
        adminEmail: settings.adminEmail || "",
        supportEmail: settings.supportEmail || "",
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (formData.adminEmail && !emailRegex.test(formData.adminEmail)) {
      newErrors.adminEmail = "Please enter a valid email address";
    }

    if (formData.supportEmail && !emailRegex.test(formData.supportEmail)) {
      newErrors.supportEmail = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    dispatch(
      updateChatbotSettings({
        adminEmail: formData.adminEmail ? formData.adminEmail.trim() : null,
        supportEmail: formData.supportEmail ? formData.supportEmail.trim() : null,
      })
    );
  };

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7" fluid>
        <Row className="justify-content-center">
          <Col lg="8" md="10">
            <Card className="shadow border-0">
              <CardHeader className="bg-white border-0 py-3">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0 text-dark font-weight-bold">
                      Chatbot Escalation Settings
                    </h3>
                    <small className="text-muted">
                      Configure recipient addresses for guest user chat escalations.
                    </small>
                  </Col>
                  <Col xs="4" className="text-right">
                    <Link to="/admin/chatbot-knowledge">
                      <Button color="secondary" size="sm">
                        <i className="fas fa-arrow-left mr-1"></i> Knowledge Base
                      </Button>
                    </Link>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="pt-2">
                <div
                  className="d-flex align-items-start mb-4 p-3 rounded"
                  style={{
                    backgroundColor: "#f0f7f7",
                    border: "1px solid #d0e7e5",
                    borderLeft: "4px solid #008080",
                  }}
                >
                  <i
                    className="fas fa-info-circle mr-3 mt-1"
                    style={{ color: "#008080", fontSize: "16px", flexShrink: 0 }}
                  />
                  <div style={{ fontSize: "13px", color: "#334e68", lineHeight: "1.5" }}>
                    <span style={{ fontWeight: "600", color: "#005c5c" }}>
                      Guest Escalations:{" "}
                    </span>
                    When a logged-out or guest visitor requests human assistance, their
                    chat transcript and contact details are dispatched to these emails.
                    Logged-in users are automatically routed to Support Tickets.
                  </div>
                </div>

                {settingsLoader ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                    <p className="mt-3 text-muted mb-0">Loading settings...</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <FormGroup>
                      <Label
                        className="form-control-label font-weight-bold"
                        htmlFor="adminEmail"
                      >
                        Admin Escalation Email
                      </Label>
                      <Input
                        id="adminEmail"
                        name="adminEmail"
                        type="email"
                        placeholder="e.g. admin@tealpot.com"
                        value={formData.adminEmail}
                        onChange={handleChange}
                        invalid={!!errors.adminEmail}
                        className="form-control-alternative"
                      />
                      {errors.adminEmail && (
                        <div className="text-danger text-xs mt-1">
                          {errors.adminEmail}
                        </div>
                      )}
                      <small className="text-muted">
                        Primary administrator address receiving escalation notices.
                      </small>
                    </FormGroup>

                    <FormGroup>
                      <Label
                        className="form-control-label font-weight-bold"
                        htmlFor="supportEmail"
                      >
                        Support Escalation Email
                      </Label>
                      <Input
                        id="supportEmail"
                        name="supportEmail"
                        type="email"
                        placeholder="e.g. support@tealpot.com"
                        value={formData.supportEmail}
                        onChange={handleChange}
                        invalid={!!errors.supportEmail}
                        className="form-control-alternative"
                      />
                      {errors.supportEmail && (
                        <div className="text-danger text-xs mt-1">
                          {errors.supportEmail}
                        </div>
                      )}
                      <small className="text-muted">
                        Customer support inbox for agent ticket follow-up.
                      </small>
                    </FormGroup>

                    {(() => {
                      const activeRecipients =
                        settings?.escalationEmails &&
                        settings.escalationEmails.length > 0
                          ? settings.escalationEmails
                          : [settings?.adminEmail, settings?.supportEmail].filter(
                              Boolean
                            );

                      return activeRecipients.length > 0 ? (
                        <div
                          className="p-3 rounded mb-4 mt-2"
                          style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e9ecef",
                          }}
                        >
                          <small className="font-weight-bold text-dark d-block mb-2">
                            <i className="fas fa-check-circle text-success mr-1" />
                            Current Active Escalation Recipients:
                          </small>
                          <div className="d-flex flex-wrap align-items-center">
                            {activeRecipients.map((email, idx) => (
                              <span
                                key={idx}
                                className="badge badge-pill mr-2 mb-1 px-3 py-2 d-inline-flex align-items-center"
                                style={{
                                  backgroundColor: "#e8f5f4",
                                  color: "#006666",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  textTransform: "none",
                                  border: "1px solid #b2dfdb",
                                  letterSpacing: "normal",
                                }}
                              >
                                <i className="fas fa-envelope mr-1 text-muted" style={{ fontSize: "11px" }} />
                                {email}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}

                    <div className="d-flex justify-content-between align-items-center pt-3 border-top mt-4">
                      <Link to="/admin/chatbot-knowledge">
                        <Button type="button" color="light" disabled={saveSettingsLoader}>
                          Cancel
                        </Button>
                      </Link>

                      <Button
                        type="submit"
                        color="primary"
                        disabled={saveSettingsLoader}
                        style={{
                          backgroundColor: "#008080",
                          borderColor: "#008080",
                        }}
                      >
                        {saveSettingsLoader ? (
                          <>
                            <Spinner size="sm" className="mr-1" /> Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save mr-1"></i> Save Settings
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ChatbotSettings;
